import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Achievement, Experience, ACHIEVEMENTS, Challenge } from '../types/gamification';
import { AchievementService } from '../services/AchievementService';
import { useProjects } from './ProjectContext';
import { PomodoroRecord } from '../types/project';
import toast from 'react-hot-toast';

const LEARN_API = import.meta.env.VITE_LEARN_API_URL || 'http://localhost:3001';
type LearnStats = { videosAnalyzed: number; questionsAsked: number; cardsReviewed: number; longestVideoSec: number };
const ZERO_LEARN_STATS: LearnStats = { videosAnalyzed: 0, questionsAsked: 0, cardsReviewed: 0, longestVideoSec: 0 };

interface GamificationContextType {
  achievements: { [key: string]: { progress: number; unlockedAt?: Date } };
  experience: Experience;
  challenges: Challenge[];
  checkAchievements: () => void;
}

const GamificationContext = createContext<GamificationContextType | undefined>(undefined);

export const GamificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pomodoros } = useProjects();
  const { getToken } = useAuth();
  const [learnStats, setLearnStats] = useState<LearnStats>(ZERO_LEARN_STATS);
  const [achievements, setAchievements] = useState<{ [key: string]: { progress: number; unlockedAt?: Date } }>({});
  const [experience, setExperience] = useState<Experience>({ currentExp: 0, level: 1, nextLevelExp: 1000 });
  const notifiedAchievements = useRef<Set<string>>(
    new Set(JSON.parse(localStorage.getItem('pomomax_notified_achievements') || '[]'))
  );
  const notifiedLevel = useRef<number>(
    parseInt(localStorage.getItem('pomomax_notified_level') || '0', 10)
  );
  const isInitialLoad = useRef(true);
  const [challenges, setChallenges] = useState<Challenge[]>([
    {
      id: 'daily-focus',
      title: 'Daily Focus',
      description: 'Complete 4 pomodoros today',
      type: 'daily',
      target: 4,
      progress: 0,
      reward: { experience: 100 },
      endDate: new Date(new Date().setHours(23, 59, 59, 999))
    },
    {
      id: 'weekly-master',
      title: 'Weekly Master',
      description: 'Complete 25 pomodoros this week',
      type: 'weekly',
      target: 25,
      progress: 0,
      reward: { experience: 500 },
      endDate: new Date(new Date().setDate(new Date().getDate() + 7))
    }
  ]);

  // Check achievements whenever pomodoros or learn stats change
  useEffect(() => {
    checkAchievements();
  }, [pomodoros, learnStats]);

  // Poll Learn stats every 60s — feeds Learn achievements
  useEffect(() => {
    let cancelled = false;
    async function fetchStats() {
      try {
        const token = await getToken();
        if (!token) return;
        const res = await fetch(`${LEARN_API}/api/learn/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as LearnStats;
        if (!cancelled) setLearnStats(data);
      } catch {
        // Learn server not running — fine, ignore
      }
    }
    fetchStats();
    const id = setInterval(fetchStats, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [getToken]);

  const checkAchievements = () => {
    try {
      // Check pomodoro-based achievements
      const pomodoroUpdates = AchievementService.checkPomodoroAchievements(pomodoros, achievements);
      
      // Calculate current streak
      const currentStreak = calculateCurrentStreak(pomodoros);
      
      // Check streak-based achievements
      const streakUpdates = AchievementService.checkStreakAchievements(currentStreak, achievements);

      // Check Learn-module achievements
      const learnUpdates = AchievementService.checkLearnAchievements(learnStats, achievements);

      // Merge all updates
      const newAchievements = {
        ...achievements,
        ...pomodoroUpdates,
        ...streakUpdates,
        ...learnUpdates,
      };

      if (isInitialLoad.current) {
        // Silently mark all currently-unlocked achievements as notified so we
        // don't spam toasts on every login/page refresh.
        Object.entries(newAchievements).forEach(([id, data]) => {
          const achievement = ACHIEVEMENTS[id as keyof typeof ACHIEVEMENTS];
          if (achievement && data.progress >= achievement.maxProgress) {
            notifiedAchievements.current.add(id);
          }
        });
        localStorage.setItem('pomomax_notified_achievements', JSON.stringify([...notifiedAchievements.current]));
      } else {
        // Show toasts only for achievements newly unlocked after initial load
        Object.entries(newAchievements).forEach(([id, data]) => {
          const achievement = ACHIEVEMENTS[id as keyof typeof ACHIEVEMENTS];
          if (achievement && data.progress >= achievement.maxProgress && !notifiedAchievements.current.has(id)) {
            notifiedAchievements.current.add(id);
            toast.success(
              <div className="flex flex-col gap-1">
                <div className="text-lg font-semibold flex items-center gap-2">
                  {achievement.icon} Achievement Unlocked!
                </div>
                <div className="font-medium">{achievement.title}</div>
                <div className="text-sm text-muted-foreground">{achievement.description}</div>
                <div className="text-sm font-medium text-primary">+{achievement.xp} XP</div>
              </div>,
              { duration: 5000, className: "achievement-toast" }
            );
          }
        });
        localStorage.setItem('pomomax_notified_achievements', JSON.stringify([...notifiedAchievements.current]));
      }

      setAchievements(newAchievements);

      // Update experience
      const newExperience = {
        currentExp: AchievementService.calculateExperience(newAchievements),
        level: Math.floor(Math.sqrt(AchievementService.calculateExperience(newAchievements) / 1000)) + 1,
        nextLevelExp: Math.pow(Math.floor(Math.sqrt(AchievementService.calculateExperience(newAchievements) / 1000)) + 2, 2) * 1000
      };
      if (!isInitialLoad.current && newExperience.level > notifiedLevel.current) {
        notifiedLevel.current = newExperience.level;
        localStorage.setItem('pomomax_notified_level', String(newExperience.level));
        toast.success(`🎊 Level Up! You're now level ${newExperience.level}`, { duration: 5000 });
      } else if (isInitialLoad.current && newExperience.level > notifiedLevel.current) {
        notifiedLevel.current = newExperience.level;
        localStorage.setItem('pomomax_notified_level', String(newExperience.level));
      }
      isInitialLoad.current = false;
      setExperience(newExperience);

      // Update challenges progress
      const todayPomodoros = pomodoros.filter(p => {
        const date = new Date(p.startTime);
        const today = new Date();
        return date.toDateString() === today.toDateString() && p.completed;
      });

      const weekPomodoros = pomodoros.filter(p => {
        const date = new Date(p.startTime);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return date >= weekAgo && p.completed;
      });

      setChallenges(prev => prev.map(challenge => {
        if (challenge.type === 'daily') {
          return {
            ...challenge,
            progress: todayPomodoros.length
          };
        }
        if (challenge.type === 'weekly') {
          return {
            ...challenge,
            progress: weekPomodoros.length
          };
        }
        return challenge;
      }));
    } catch (error) {
      console.error('Error checking achievements:', error);
    }
  };

  return (
    <GamificationContext.Provider value={{ 
      achievements, 
      experience, 
      challenges, 
      checkAchievements 
    }}>
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (context === undefined) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

// Helper function to calculate current streak
const calculateCurrentStreak = (pomodoros: PomodoroRecord[]): number => {
  if (pomodoros.length === 0) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Sort pomodoros by date
  const sortedPomodoros = [...pomodoros]
    .sort((a, b) => b.startTime - a.startTime)
    .filter(p => p.completed);

  // Get unique dates
  const uniqueDates = new Set(
    sortedPomodoros.map(p => {
      const date = new Date(p.startTime);
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    })
  );

  let streak = 0;
  let currentDate = today.getTime();

  // Start from today; if no pomodoros today, try yesterday
  if (!uniqueDates.has(currentDate)) {
    currentDate -= 24 * 60 * 60 * 1000;
  }

  // Count consecutive days
  while (uniqueDates.has(currentDate)) {
    streak++;
    currentDate -= 24 * 60 * 60 * 1000;
  }

  return streak;
};